import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import DialogShell from '../../components/DialogShell';
import { listProductEvaluations, recomputeEvaluations, buyNow, getPurchasesStats } from '../../services/cloneProvider';
import { getErrorMessage, formatVnd, formatDateTime } from './badges';
import useUrlState from './useUrlState';

const PRODUCT_EVAL_STALE_MS = 5 * 60 * 1000;
const SERVICE_TYPE_LABEL = { fb_ql: 'FB QL', fb_eaaaau: 'FB EAAAAU', fb_eaagno: 'FB EAAGNO' };

function BuyDialog({ website, externalId, productName, onClose, onDone }) {
  const [amount, setAmount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const record = await buyNow({ website, externalId, amount: Number(amount) });
      setResult(record);
      toast.success('Mua thành công.');
      onDone();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <div className="w-80 max-w-full">
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Mua ngay</h3>
        <p className="mb-3 text-xs text-slate-500">{productName} — {website}</p>
        {result ? (
          <div className="space-y-2 text-sm">
            <p>Đã mua {result.account_count} account, tổng {formatVnd(result.total_vnd)}.</p>
            <button type="button" onClick={onClose} className="rounded-lg bg-cyan-600 px-3 py-2 text-sm text-white">
              Đóng
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              Đây là giao dịch THẬT, sẽ trừ tiền tài khoản marketplace.
            </div>
            <input
              required
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Đang mua...' : `Xác nhận mua ${amount} account`}
            </button>
          </form>
        )}
      </div>
    </DialogShell>
  );
}

const EVALUATION_COLUMNS = [
  { key: 'normalized_name', label: 'Sản phẩm', sortable: false },
  { key: 'website', label: 'Website', sortable: false },
  { key: 'price', label: 'Giá', sortable: true },
  { key: 'in_stock', label: 'Tồn kho', sortable: true },
  { key: 'est_cost_vnd', label: 'Est. cost', sortable: true },
  { key: 'avg_cost_vnd', label: 'Avg cost', sortable: true },
  { key: 'last_purchased_at', label: 'Lần mua gần nhất', sortable: true },
  { key: 'updated_at', label: 'Cập nhật', sortable: true },
];

export default function ProductEvaluationsTab() {
  const [evaluations, setEvaluations] = useState(null);
  const [requestByServiceType, setRequestByServiceType] = useState(null);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [buyTarget, setBuyTarget] = useState(null);
  const [inStockOnlyStr, setInStockOnlyStr] = useUrlState('evaluations', 'in_stock_only', 'false');
  const inStockOnly = inStockOnlyStr === 'true';
  const setInStockOnly = (next) => setInStockOnlyStr(String(typeof next === 'function' ? next(inStockOnly) : next));
  const [sortBy, setSortBy] = useUrlState('evaluations', 'sort_by', 'est_cost_vnd');
  const [sortAscStr, setSortAscStr] = useUrlState('evaluations', 'sort_asc', 'true');
  const sortAsc = sortAscStr === 'true';
  const setSortAsc = (next) => setSortAscStr(String(typeof next === 'function' ? next(sortAsc) : next));
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = { sort_by: sortBy, sort_asc: sortAsc };
      if (inStockOnly) params.in_stock = true;
      const [evalData, purchasesStats] = await Promise.all([
        listProductEvaluations(params),
        getPurchasesStats(),
      ]);
      setEvaluations(evalData);
      setRequestByServiceType(purchasesStats.total_request_by_service_type);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [inStockOnly, sortBy, sortAsc]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function handleSort(column) {
    if (!column.sortable) return;
    if (sortBy === column.key) setSortAsc((v) => !v);
    else {
      setSortBy(column.key);
      setSortAsc(true);
    }
  }

  async function handleRecompute() {
    setIsRecomputing(true);
    try {
      await recomputeEvaluations();
      toast.success('Đã tính lại.');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsRecomputing(false);
    }
  }

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsRefreshing(false);
    }
  }

  const isStale =
    evaluations &&
    evaluations.length > 0 &&
    evaluations.every((e) => Date.now() - new Date(`${e.updated_at}Z`).getTime() > PRODUCT_EVAL_STALE_MS);

  return (
    <div className="space-y-3">

      <div className="flex items-center justify-between rounded-xl border border-cyan-100 bg-white p-3">
        <div className="flex items-center gap-4">
          <p className="text-sm text-slate-600">{evaluations ? `${evaluations.length} sản phẩm đang theo dõi` : 'Đang tải...'}</p>
          <label className="flex items-center gap-1.5 text-sm text-slate-700">
            <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} />
            Chỉ còn hàng
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Tải lại dữ liệu đang hiển thị (không tính lại giá/tồn kho)"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {isRefreshing ? 'Đang tải...' : 'Tải lại'}
          </button>
          <button
            type="button"
            onClick={handleRecompute}
            disabled={isRecomputing}
            className="rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            {isRecomputing ? 'Đang tính...' : 'Tính lại ngay'}
          </button>
        </div>
      </div>

      {isStale && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Dữ liệu product evaluation có vẻ đã cũ — kiểm tra job product_evaluation ở tab Config.
        </div>
      )}

      <div className="max-h-[60vh] overflow-auto rounded-2xl border border-cyan-100 bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              {EVALUATION_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  onClick={() => handleSort(column)}
                  className={`px-3 py-2 ${column.sortable ? 'cursor-pointer select-none hover:text-slate-700' : ''}`}
                >
                  {column.label}
                  {column.sortable && sortBy === column.key && (sortAsc ? ' ▲' : ' ▼')}
                </th>
              ))}
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(evaluations || []).map((e) => (
              <tr key={`${e.website}-${e.normalized_name}`} className="border-t border-slate-100">
                <td className="px-3 py-2">{e.normalized_name}</td>
                <td className="px-3 py-2">{e.website}</td>
                <td className="px-3 py-2">{formatVnd(e.price)}</td>
                <td className="px-3 py-2">
                  {e.in_stock > 0 ? e.in_stock : <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">0</span>}
                </td>
                <td className="px-3 py-2">{formatVnd(e.est_cost_vnd)}</td>
                <td className="px-3 py-2">{formatVnd(e.avg_cost_vnd)}</td>
                <td className="px-3 py-2">{formatDateTime(e.last_purchased_at)}</td>
                <td className="px-3 py-2">{formatDateTime(e.updated_at)}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    disabled={e.external_product_id == null}
                    onClick={() => setBuyTarget(e)}
                    className="text-xs font-medium text-cyan-700 hover:underline disabled:opacity-30"
                  >
                    Mua ngay
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {buyTarget && (
        <BuyDialog
          website={buyTarget.website}
          externalId={buyTarget.external_product_id}
          productName={buyTarget.normalized_name}
          onClose={() => setBuyTarget(null)}
          onDone={fetchData}
        />
      )}
    </div>
  );
}
